import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { compose } from 'ramda';
import { CircularProgress } from '@material-ui/core/';
import queryString from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import { showToast, Typography, Button, BackTo } from '@deeptrue-frontend/ui';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import ProjectMenu from '../Common/ProjectMenu';
import { withProject, WithProjectProps } from '../withProject';
import { useFormatMessage } from 'components/IntlProvider';
import { Dashboard } from 'components/Layout';
import { OrganizationType } from 'store/modules/auth/auth';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import api from 'api/api';
import env from 'consts/env';

const { connect, selectors } = StoreProvider;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5rem;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ProjectMenuWrapper = styled.div`
  width: 100%;
`;

const Cta = styled(Button)`
  margin-top: 2rem;
`;

interface Props extends RouteComponentProps<{ id: string; codename: string }> {
  organization: OrganizationType;
  project: WithProjectProps['project'];
  isBexioUser: boolean;
}

const Subscriptions = ({
  location,
  history,
  match: { params },
  organization,
  project,
  isBexioUser,
}: Props) => {
  const t = useFormatMessage();
  const orgName = params.codename || organization.codename;
  const stripePromise = loadStripe(env.STRIPE_KEY || '');
  const { subscription } = project;

  const isSubscriptionActivated = !!subscription;

  const humanizeStatus = {
    /* i18n */
    active: t(
      'project.customerSatisfaction.payments.subscriptions.status.active',
      'Active'
    ),
    paused: t(
      'project.customerSatisfaction.payments.subscriptions.status.paused',
      'Paused'
    ),
    card_failure: t(
      'project.customerSatisfaction.payments.subscriptions.status.card_failure',
      'Credit card failure'
    ),
    canceled: t(
      'project.customerSatisfaction.payments.subscriptions.status.canceled',
      'Canceled'
    ),
  };

  const subscribeToService = () => {
    return api.resources.payments
      .subscribe({ projectId: project.id, orgName })
      .then(async ({ data }) => {
        const { session_id } = data;
        const stripe = (await stripePromise) as Stripe;

        await stripe.redirectToCheckout({
          sessionId: session_id,
        });
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t('errors.toast.payment', 'Payment failed'),
        });
      });
  };

  useEffect(() => {
    if (project.id !== -1) {
      const { add_card } = queryString.parse(location.search);
      if (add_card && !isSubscriptionActivated) {
        subscribeToService();
      }
    }
    // eslint-disable-next-line
  }, [project]);
  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;

  const isProjectLoaded = project.id !== -1;
  const status = subscription && humanizeStatus[subscription.status];
  return (
    <Dashboard title="Subscriptions" location={location} isFullWidth>
      {isBexioUser && (
        <BackTo onClick={() => history.push(overviewUrl)}>
          {t('project.backTo.overview', 'Back to overview')}
        </BackTo>
      )}
      <ProjectMenuWrapper>
        <ProjectMenu
          id={project.id}
          projectStatus={status}
          isProjectSubscribable={true}
          isProjectLoaded={project.id !== -1}
        />
      </ProjectMenuWrapper>
      {!isProjectLoaded ? (
        <LoaderWrapper>
          <CircularProgress size={80} color="primary" />
        </LoaderWrapper>
      ) : isSubscriptionActivated ? (
        <>
          <Typography variant="copy1" weight="medium" mt="1rem">
            {t(
              'project.customerSatisfaction.payments.subscriptions.status.title',
              'Subscription status:'
            )}
          </Typography>

          <Typography ml="0.5rem" variant="copy1" weight="bold" mt="1rem">
            {status}
          </Typography>
        </>
      ) : (
        <Cta id="add_credit_card" onClick={() => subscribeToService()}>
          {t(
            'project.customerSatisfaction.payments.card.add',
            'Add your credit card details'
          )}
        </Cta>
      )}
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
  })),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: Props) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(Subscriptions);
